import { useState, type FormEvent } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Link as RouterLink } from 'react-router-dom'
import { Banner, Button, Input, Radio, useKumoToastManager } from '@cloudflare/kumo'
import { CheckIcon } from '@phosphor-icons/react'
import type { GuestInvitationCreateResult, RecentGuest, RoomBookingDto } from '@hub-wifi/shared'
import { createGuestInvitation } from '../../api/guestInvitations'
import { useTranslation } from '../../i18n'
import { todayYmdInTz } from '../../lib/calendar-time'
import { formatWeekdayDate } from '../../lib/format'
import {
  checkVisitWindow,
  defaultVisitWindow,
  bookingWindow,
  describeGuestInvitationError,
  EMAIL_RE,
  formatStoredPhoneForDisplay,
  maskGuestPhoneInput,
  normalizeGuestPhone,
  type VisitWindow,
} from '../../lib/guest-invitation-form'
import { HomeSheet } from './HomeSheet'
import { shareLink } from './share'

const MAX_RECENT_CHIPS = 6

export interface InviteSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  timeZone: string
  now: Date
  /** Booking to link the guest to. When set, "With my booking" is the default. */
  booking: RoomBookingDto | null
  recentGuests: RecentGuest[]
  /** Pre-fill name/contact (an "Invite again" chip). */
  initialGuest?: RecentGuest | null
  onCreated: (invitation: GuestInvitationCreateResult) => void
}

type When = 'booking' | 'other'

interface GuestFields {
  name: string
  email: string
  phone: string
}

function fieldsFor(guest: RecentGuest | null | undefined): GuestFields {
  return guest
    ? {
        name: guest.guestName,
        email: guest.guestEmail,
        phone: formatStoredPhoneForDisplay(guest.guestPhone),
      }
    : { name: '', email: '', phone: '' }
}

/** "Invite a guest": recent-guest chips, name/contact, with my booking or another time. */
export function InviteSheet(props: InviteSheetProps) {
  const { t } = useTranslation()
  const [sent, setSent] = useState(false)
  return (
    <HomeSheet
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={sent ? t('dashboard.inviteSheet.sentTitle') : t('dashboard.inviteSheet.title')}
    >
      <InviteSheetBody {...props} onSentChange={setSent} />
    </HomeSheet>
  )
}

function InviteSheetBody({
  timeZone,
  now,
  booking,
  recentGuests,
  initialGuest,
  onCreated,
  onSentChange,
}: InviteSheetProps & { onSentChange: (sent: boolean) => void }) {
  const { t, locale } = useTranslation()
  const { getAccessTokenSilently } = useAuth0()
  const toasts = useKumoToastManager()

  const [fields, setFields] = useState<GuestFields>(() => fieldsFor(initialGuest))
  const [when, setWhen] = useState<When>(booking ? 'booking' : 'other')
  const [visit, setVisit] = useState<VisitWindow>(() => defaultVisitWindow(timeZone, now))
  const [errors, setErrors] = useState<{ name?: string; email?: string; phone?: string }>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [created, setCreated] = useState<GuestInvitationCreateResult | null>(null)

  const withBooking = when === 'booking' && booking !== null
  const visitCheck = withBooking ? null : checkVisitWindow(visit, timeZone, now)
  const roomName = booking?.roomName ?? t('dashboard.nextUp.room')

  function dayLabel(ymd: string): string {
    const today = todayYmdInTz(timeZone, now)
    const date = formatWeekdayDate(ymd, locale ?? 'pt-BR')
    if (ymd === today) return t('dashboard.agenda.today')
    return date
  }

  function windowLabel(w: VisitWindow): string {
    return `${dayLabel(w.visitDate)} ${w.visitTimeBegin} – ${w.visitTimeEnd}`
  }

  function pickRecent(guest: RecentGuest) {
    setFields(fieldsFor(guest))
    setErrors({})
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault()
    const next: typeof errors = {}
    if (fields.name.trim() === '') next.name = t('dashboard.inviteSheet.nameRequired')
    const email = fields.email.trim()
    if (email !== '' && !EMAIL_RE.test(email)) next.email = t('dashboard.inviteSheet.invalidEmail')
    const phone = normalizeGuestPhone(fields.phone)
    if (!phone.ok) next.phone = t('enroll.validation.invalidMobile')
    setErrors(next)
    if (Object.keys(next).length > 0 || !phone.ok) return
    if (visitCheck?.errorKey) return

    const visitFields = withBooking && booking ? bookingWindow(booking, timeZone) : visit
    setSubmitting(true)
    setSubmitError(null)
    try {
      const token = await getAccessTokenSilently()
      const dto = await createGuestInvitation(
        {
          guestName: fields.name.trim(),
          guestEmail: email || null,
          guestPhone: phone.e164,
          ...visitFields,
          language: locale,
          ...(withBooking && booking ? { roomBookingId: booking.id } : {}),
        },
        token,
      )
      setCreated(dto)
      onSentChange(true)
      onCreated(dto)
    } catch (err) {
      setSubmitError(describeGuestInvitationError(err, t))
    } finally {
      setSubmitting(false)
    }
  }

  function inviteSomeoneElse() {
    setCreated(null)
    onSentChange(false)
    setFields(fieldsFor(null))
    setErrors({})
  }

  async function onShare(url: string, copyOnly: boolean) {
    const outcome = copyOnly
      ? await navigator.clipboard.writeText(url).then(
          () => 'copied' as const,
          () => 'failed' as const,
        )
      : await shareLink(url)
    if (outcome === 'copied') {
      toasts.add({ variant: 'success', title: t('dashboard.agenda.linkCopied') })
    } else if (outcome === 'failed') {
      toasts.add({ variant: 'error', title: t('dashboard.agenda.shareFailed') })
    }
  }

  const fullFormHref =
    withBooking && booking
      ? `/my/guests/new?roomBookingId=${encodeURIComponent(booking.id)}`
      : '/my/guests/new'

  if (created) {
    const createdWindow = windowLabel(created)
    return (
      <div className="home-sheet__stack">
        <div className="home-success" role="status">
          <span className="home-success__icon" aria-hidden>
            <CheckIcon size={18} weight="bold" />
          </span>
          <div className="home-row__text">
            <span className="home-row__title">
              {t('dashboard.inviteSheet.invited', { name: created.guestName })}
            </span>
            <span className="home-success__sub">
              {created.roomBookingId
                ? t('dashboard.inviteSheet.linkedTo', { window: createdWindow, room: roomName })
                : createdWindow}
            </span>
          </div>
        </div>
        {created.guestEmailSent === true && (
          <p className="text-sm text-kumo-subtle">
            {t('guestInvitations.success.emailSent', { email: created.guestEmail ?? '' })}
          </p>
        )}
        {created.guestEmailSent === false && (
          <Banner variant="alert">
            {t('dashboard.inviteSheet.emailFailed', { email: created.guestEmail ?? '' })}
          </Banner>
        )}
        <div className="home-sheet__actions">
          <Button
            variant="primary"
            className="home-sheet__btn"
            onClick={() => void onShare(created.invitationUrl, false)}
          >
            {t('dashboard.inviteSheet.shareLink')}
          </Button>
          <Button
            variant="secondary"
            className="home-sheet__btn"
            onClick={() => void onShare(created.invitationUrl, true)}
          >
            {t('dashboard.inviteSheet.copyLink')}
          </Button>
        </div>
        <Button variant="ghost" className="home-sheet__btn" onClick={inviteSomeoneElse}>
          {t('dashboard.inviteSheet.inviteSomeoneElse')}
        </Button>
      </div>
    )
  }

  const recent = recentGuests.slice(0, MAX_RECENT_CHIPS)

  return (
    <form className="home-sheet__stack" onSubmit={(e) => void onSubmit(e)} noValidate>
      {recent.length > 0 && (
        <fieldset className="home-fieldset">
          <legend className="home-field-label">{t('dashboard.inviteSheet.recentGuests')}</legend>
          <div className="home-chip-scroller">
            {recent.map((g) => (
              <Button
                key={g.guestEmail}
                variant="secondary"
                className="home-chip-toggle"
                aria-pressed={fields.email === g.guestEmail && fields.name === g.guestName}
                onClick={() => pickRecent(g)}
              >
                {g.guestName}
              </Button>
            ))}
          </div>
        </fieldset>
      )}

      <Input
        label={t('guestInvitations.form.guestName')}
        className="home-control"
        autoComplete="off"
        value={fields.name}
        error={errors.name}
        aria-invalid={errors.name ? true : undefined}
        onChange={(e) => setFields((f) => ({ ...f, name: e.target.value }))}
      />
      <div className="home-field-row">
        <div className="home-field">
          <Input
            label={t('guestInvitations.form.guestEmail')}
            className="home-control"
            type="email"
            inputMode="email"
            autoComplete="off"
            value={fields.email}
            error={errors.email}
            aria-invalid={errors.email ? true : undefined}
            onChange={(e) => setFields((f) => ({ ...f, email: e.target.value }))}
          />
        </div>
        <div className="home-field">
          <Input
            label={t('guestInvitations.form.guestPhone')}
            className="home-control"
            type="tel"
            inputMode="tel"
            placeholder="+55 11 99999-0000"
            value={fields.phone}
            error={errors.phone}
            aria-invalid={errors.phone ? true : undefined}
            onChange={(e) => {
              const raw = e.target.value
              setFields((f) => ({ ...f, phone: maskGuestPhoneInput(f.phone, raw) }))
              if (errors.phone) setErrors((x) => ({ ...x, phone: undefined }))
            }}
          />
        </div>
      </div>
      <p className="text-sm text-kumo-subtle">{t('dashboard.inviteSheet.contactHint')}</p>

      <div className="home-fieldset">
        <Radio.Group
          legend={t('dashboard.bookSheet.when')}
          appearance="card"
          className="home-radio-group"
          value={withBooking ? 'booking' : 'other'}
          onValueChange={(v) => setWhen(v as When)}
        >
          {booking && (
            <Radio.Item
              value="booking"
              label={t('dashboard.inviteSheet.withBooking')}
              description={`${roomName} · ${windowLabel(bookingWindow(booking, timeZone))}`}
            />
          )}
          <Radio.Item
            value="other"
            label={t('dashboard.inviteSheet.anotherTime')}
            description={t('dashboard.inviteSheet.anotherTimeHint')}
          />
        </Radio.Group>
        {!withBooking && (
          <div className="home-field-row home-field-row--3">
            <div className="home-field">
              {/* Native date/time inputs (Kumo `Input`), as on the mobile enroll
                  form: phones show their own picker. */}
              <Input
                type="date"
                label={t('guestInvitations.form.visitDate')}
                className="home-control"
                value={visit.visitDate}
                min={visitCheck?.todayYmd}
                onChange={(e) => setVisit((v) => ({ ...v, visitDate: e.target.value }))}
              />
            </div>
            <div className="home-field">
              <Input
                type="time"
                label={t('guestInvitations.form.visitTimeBegin')}
                className="home-control"
                value={visit.visitTimeBegin}
                onChange={(e) => setVisit((v) => ({ ...v, visitTimeBegin: e.target.value }))}
              />
            </div>
            <div className="home-field">
              <Input
                type="time"
                label={t('guestInvitations.form.visitTimeEnd')}
                className="home-control"
                value={visit.visitTimeEnd}
                onChange={(e) => setVisit((v) => ({ ...v, visitTimeEnd: e.target.value }))}
              />
            </div>
          </div>
        )}
        {visitCheck?.errorKey && <Banner variant="error">{t(visitCheck.errorKey)}</Banner>}
        {visitCheck?.beginsNow && (
          <p className="text-sm text-kumo-subtle">
            {t('guestInvitations.form.beginsNow', { time: visitCheck.nowHm })}
          </p>
        )}
      </div>

      {submitError && <Banner variant="error">{submitError}</Banner>}

      <Button
        type="submit"
        variant="primary"
        className="home-sheet__btn"
        loading={submitting}
        disabled={submitting || Boolean(visitCheck?.errorKey)}
      >
        {submitting ? t('guestInvitations.form.submitting') : t('dashboard.inviteSheet.send')}
      </Button>
      <RouterLink to={fullFormHref} className="home-sheet__link">
        {t('dashboard.inviteSheet.moreOptions')}
      </RouterLink>
    </form>
  )
}
